/**
 * GROWTH-01 — the browser half of the banner's session.
 *
 * src/lib/appBanner.ts decides what a stored value MEANS; this file is the only
 * code that reads or writes it. Two stores, two reasons:
 *
 *   `sessionStorage`, APP_BANNER_SESSION_KEY — the session itself. Scoped by
 *   the browser to one tab, which is what makes a new tab, or the site opened
 *   again after the tab was closed, a new visit with no answer on record. The
 *   idle timeout (src/lib/appBanner.ts, SESSION_IDLE_TIMEOUT_MS) covers the
 *   case sessionStorage cannot: a tab left open on a phone for hours.
 *
 *   `localStorage`, APP_BANNER_INSTALL_HINT_KEY — the install hint. Meant to
 *   outlive every session: a phone that tapped "Get the app" last month is the
 *   phone most likely to have it this month.
 *
 * Both stores can throw on access alone (Safari with site data blocked, a
 * full quota). The house rule (src/components/home/useClientOnlyStorage.ts)
 * applies throughout: a prompt one visitor sees twice is recoverable, a prompt
 * nobody ever sees is not — so an unreadable store is an unanswered session,
 * and an unwritable one means the banner is gone for this pageview only.
 *
 * Nothing here runs at import time. src/pages/property/@slug/ is server-
 * rendered, and every function below is only ever called from an effect or an
 * event handler.
 */

import {
  APP_BANNER_INSTALL_HINT_KEY,
  APP_BANNER_SESSION_KEY,
  LEGACY_COOLDOWN_KEY,
  SESSION_HEARTBEAT_MS,
  answerSession,
  isAnsweredInSession,
  readSession,
  serializeSession,
  touchSession,
  type AppBannerAnswerReason,
  type AppBannerSession,
} from './appBanner';

function readSessionRaw(): string | null {
  try {
    return window.sessionStorage.getItem(APP_BANNER_SESSION_KEY);
  } catch {
    return null;
  }
}

function writeSession(session: AppBannerSession): void {
  try {
    window.sessionStorage.setItem(APP_BANNER_SESSION_KEY, serializeSession(session));
  } catch {
    // Unwritable: the banner is still gone for this pageview, it just cannot
    // be gone for the next one. Never a reason to fail the click.
  }
}

/** Has the visitor already answered the banner in the session running now? */
export function isAnsweredThisSession(now: number = Date.now()): boolean {
  return isAnsweredInSession(readSessionRaw(), now);
}

/**
 * A sign of life. Renews the running session, or — when the one on record has
 * ended — begins a new, unanswered one. `readSession` is what decides which,
 * so the rollover happens here and nowhere else.
 */
export function touchAppBannerSession(now: number = Date.now()): AppBannerSession {
  const session = touchSession(readSession(readSessionRaw(), now), now);
  writeSession(session);
  return session;
}

/** The visitor answered: the ✕ or the CTA. Silences the banner for the rest of this session. */
export function answerAppBannerSession(reason: AppBannerAnswerReason, now: number = Date.now()): void {
  writeSession(answerSession(now, reason));
}

/** The install hint — see `bannerVariant` in src/lib/appBanner.ts. */
export function hasTappedCtaBefore(): boolean {
  try {
    return window.localStorage.getItem(APP_BANNER_INSTALL_HINT_KEY) !== null;
  } catch {
    return false;
  }
}

export function rememberCtaTap(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(APP_BANNER_INSTALL_HINT_KEY, String(now));
  } catch {
    // Not remembered: the next session shows "get" rather than "open". Recoverable.
  }
}

/**
 * Keeps `lastSeenAt` honest for as long as a page is open, so that a session
 * ends thirty minutes after the visitor LEFT — not thirty minutes after they
 * answered the banner, and not never.
 *
 *   on start          — a sign of life, and the one place the pre-session
 *                       cooldown key is tidied out of localStorage.
 *   every minute      — while the page is visible. A visitor reading one long
 *                       listing without navigating is still here.
 *   visibilitychange  — leaving stamps the moment they left, which is where the
 *                       idle clock starts; returning renews the session if it
 *                       is still running, and begins a new one if it is not.
 *   pagehide          — closing the tab or navigating away, for browsers that
 *                       do not fire visibilitychange on the way out.
 *
 * Mounted once, in src/pages/+Layout.tsx, above both client routers — so it
 * runs on every route the web app can render, not only the ones that carry
 * the banner. A dismissal on a listing followed by forty minutes on the home
 * page is still one uninterrupted visit.
 *
 * Returns the function that stops it.
 */
export function startAppBannerSessionHeartbeat(): () => void {
  if (typeof window === 'undefined') return () => {};

  try {
    window.localStorage.removeItem(LEGACY_COOLDOWN_KEY);
  } catch {
    // Nothing to tidy, or nowhere to tidy it.
  }

  const beat = () => {
    if (document.visibilityState !== 'hidden') touchAppBannerSession();
  };
  const onLeaveOrReturn = () => {
    touchAppBannerSession();
  };

  beat();
  const timer = window.setInterval(beat, SESSION_HEARTBEAT_MS);
  document.addEventListener('visibilitychange', onLeaveOrReturn);
  window.addEventListener('pagehide', onLeaveOrReturn);

  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onLeaveOrReturn);
    window.removeEventListener('pagehide', onLeaveOrReturn);
  };
}
